import React, { useEffect, useRef } from 'react'

import { useTranslation } from 'react-i18next'

import APIErrorToast from '@components/APIErrorToast'
import OfficialBadge from '@components/music/OfficialBadge'
import PurchasingSpinner from '@components/music/title-details/purchase-title-dialog/PurchasingSpinner'
import SignInRequired from '@components/music/title-details/purchase-title-dialog/SignInRequired'
import CoverArtImage from '@components/shared/CoverArt/CoverArtImage'
import Dialog from '@components/shared/Dialog'
import DialogButtons from '@components/shared/DialogButtons'
import { useTitle } from '@providers/TitleProvider'
import { useDialogStore } from '@stores/dialog.store'
import Tippy from '@tippyjs/react'
import Storage from '@utils/storage'

import PaymentForm from '../../PaymentForm/PaymentForm'
import PriceInfo from '../purchased-product/PriceInfo'
import SelectAuthorizedPerformers from '../purchased-product/SelectAuthorizedPerformers'
import PurchaseCompleteDialog from './PurchaseCompleteDialog'
import PurchaseErrorDialog from './PurchaseErrorDialog'
import usePurchaseTitleDialog, { DialogState } from './hooks/usePurchaseTitleDialog'

interface Props {
  initialAuthorizedPerformers?: number | null
}

const PurchaseTitleDialog: React.FC<Props> = ({ initialAuthorizedPerformers }) => {
  const { t } = useTranslation()
  const { title } = useTitle()
  const openDialog = useDialogStore((s) => s.openDialog)

  const buyerSignInQuantity = useRef(Storage.getPurchasingQuantity(title.uuid))

  // Open dialog if user just signed up / in
  useEffect(() => {
    if (buyerSignInQuantity.current) {
      openDialog('purchase_title_dialog')

      // Clear stored value
      Storage.setPurchasingQuantity(title.uuid, null)
    }
  }, [])

  return (
    <>
      <Dialog
        name="purchase_title_dialog"
        className="relative"
        title={title.isOwnedByUser() ? t('action.addMore') : t('action.purchaseScore')}
        maxWidth={700}>
        <PurchaseTitleDialogBody
          initialAuthorizedPerformers={initialAuthorizedPerformers ?? buyerSignInQuantity.current}
        />
      </Dialog>
      <PurchaseCompleteDialog />
    </>
  )
}

export default PurchaseTitleDialog

const PurchaseTitleDialogBody: React.FC<Props> = ({ initialAuthorizedPerformers }) => {
  const { t } = useTranslation()
  const { title } = useTitle()

  const {
    uiState,
    dialogErrorCode,
    okButton,
    cancelButton,
    paymentFormRef,
    selectedAuthorizedPerformers,
    setSelectedAuthorizedPerformers,
    setIsValid,
    setTaxApiParams,
    onErrorOk,
    price,
    clientSecret,
  } = usePurchaseTitleDialog({ initialAuthorizedPerformers })

  return (
    <>
      {/* toast catches misc server errors */}
      <APIErrorToast headerVisible={true} />

      {/* dialog for longer error messages from payment response */}
      {uiState == DialogState.ERROR && dialogErrorCode && (
        <>
          <PurchaseErrorDialog errorCode={dialogErrorCode} onClose={onErrorOk} />
        </>
      )}

      {/* Title Details / Price Header */}
      <div className="flex flex-wrap justify-between w-full">
        <div className="flex w-full pb-6 sm:w-64">
          <CoverArtImage title={title} className="w-[70px] h-[70px] shrink-0" />
          <div className="ml-4 overflow-hidden">
            <div className="flex flex-row flex-nowrap">
              <p className="text-md w-full mb-0.75 mr-1">
                {title.title}
                {!title.metadata.usergen && <OfficialBadge />}
              </p>
            </div>
            {title.metadata.primary_contributor && (
              <p className="md:text-md font-thin ellipsify mb-0.75">
                {title.metadata.primary_contributor}
              </p>
            )}
            <p className="md:text-md font-semibold mb-0.5">{title.formattedUnitPrice()}</p>
          </div>
        </div>

        <SelectAuthorizedPerformers
          className="flex self-start pb-6 mr-5"
          title={title}
          value={selectedAuthorizedPerformers}
          setValue={setSelectedAuthorizedPerformers}>
          <>
            <div className="flex">
              <div className="w-32">
                <span className="font-semibold text-md">
                  {t('content.authorizedPerformers.title')}
                </span>
                <Tippy
                  arrow
                  interactive
                  maxWidth={270}
                  placement="bottom"
                  content={t('hint.purchaseScore.authorizedPerformers.maxIncluded')}
                  trigger="mouseenter">
                  <i className="ml-1 cursor-pointer nf-icon nf-help-sign text-gray-icons dark:text-gray-icons-dm" />
                </Tippy>
                <div className="mb-2 text-gray-text">
                  {title.isOwnedByUser() && (
                    <span>{t('content.authorizedPerformers.alreadyOwned')} </span>
                  )}
                </div>
              </div>
            </div>
          </>
        </SelectAuthorizedPerformers>
        <PriceInfo className="pb-6" price={price} />
      </div>

      {/* Payment Method Body */}
      <div className="flex flex-col justify-between pt-5 border-t border-gray dark:border-gray-border-dm bg-gray-light dark:bg-black-bg min-h-[20rem] px-6 pb-6 -mx-5 -mb-5">
        <div>
          {uiState === DialogState.SIGN_IN_REQUIRED && (
            <SignInRequired purchaseQuantity={selectedAuthorizedPerformers} titleId={title.uuid} />
          )}
          {uiState === DialogState.SUBMITTED && <PurchasingSpinner />}

          {/* leave PaymentForm rendered while submitting to keep state in event of card error */}
          {(uiState === DialogState.READY || uiState === DialogState.SUBMITTED) && (
            <PaymentForm
              ref={paymentFormRef}
              clientSecret={clientSecret}
              price={price}
              setTaxApiParams={setTaxApiParams}
              onChangeValidity={setIsValid}
            />
          )}

          {uiState === DialogState.COMPLETED && (
            <>
              <p className="font-semibold">{t('content.thanksForPurchasing')}</p>
              <p>{t('content.purchasedScoreAvailable')}</p>
            </>
          )}
        </div>

        {uiState !== DialogState.SIGN_IN_REQUIRED && (
          <DialogButtons
            dialogName="purchase_title_dialog"
            okBtn={okButton}
            cancelBtn={cancelButton}
          />
        )}
      </div>
    </>
  )
}
